import { inject, Injectable, signal } from '@angular/core';
import { BillingAPIService } from '@core/billing/billing.api';
import {
  AssignSeatRequest,
  CRSubscription,
  SubscriptionResponse,
  UpgradeSubscriptionRequest,
} from '@core/billing/billing.models';
import { ErrorHandler } from '@core/handler/error.handler';
import { ChargeRepayErrorCodes } from '@shared/errors/error.codes';
import { CRAPIError, CRError } from '@shared/models/error.models';
import { catchError, concat, EMPTY, ignoreElements, Observable, tap, throwError } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class BillingStore {
  private readonly api = inject(BillingAPIService);
  private readonly errorHandler = inject(ErrorHandler);

  public readonly isInitialized = signal(false);
  public readonly subscription = signal<CRSubscription | null>(null);

  public initialize() {
    if (this.isInitialized()) {
      return EMPTY as Observable<void>;
    }

    return concat(this.createSubscriptions()).pipe(
      tap({ complete: () => this.isInitialized.set(true) }),
      catchError((error) => {
        console.error('Error initializing billing store:', error);
        return throwError(() => error);
      }),
    );
  }

  public reinitialize() {
    return this.createSubscriptions().pipe(
      catchError((error) => {
        console.error('Error reinitializing billing store:', error);
        return throwError(() => error);
      }),
    );
  }

  // Uses the first subscription until the API returns a single one.
  public createSubscriptions() {
    return this.api.getSubscriptions().pipe(
      tap((subscriptions) => {
        if (subscriptions[0]) {
          const parsedSub = this.parseSubscriptionResponse(subscriptions[0]);
          this.subscription.set(parsedSub);
        }
      }),
      catchError(() => {
        this.subscription.set(null);
        this.handleSubscriptionError();
        return EMPTY;
      }),
      ignoreElements(),
    );
  }

  private parseSubscriptionResponse(response: SubscriptionResponse): CRSubscription {
    return {
      id: response.id,
      smartServiceId: response.smart_service_id,
      bookedPackageId: response.booked_package_id,
      startDate: response.start_date,
      endDate: response.end_date,
      trialEndDate: response.trial_end_date,
      quantity: response.quantity,
      referenceCode: response.reference_code,
      internalOrderId: response.internal_order_id ?? '',
      nextSubscriptionPhase: response.next_subscription_phase,
      inTrialPeriod: response.in_trial_period,
      seatsUsed: response.seats_used,
      seatsMaxCount: response.seats_max_count,
      seatsFreeCount: response.seats_free_count,
    };
  }

  private handleSubscriptionError(): void {
    const getSubscriptionsError: CRError = {
      errorCode: ChargeRepayErrorCodes.GET_SUBSCRIPTIONS_ERROR,
      severity: 'panic',
    };
    this.errorHandler.handleError(getSubscriptionsError);
  }

  public upgradeSubscription(subscriptionId: string, seatCount: number) {
    const request: UpgradeSubscriptionRequest = {
      seat_count: seatCount,
    };

    return this.api.upgradeSubscription(subscriptionId, request).pipe(
      tap((updatedSubscription) => {
        const parsedSub = this.parseSubscriptionResponse(updatedSubscription);
        this.subscription.set(parsedSub);
      }),
      catchError((_error: CRAPIError) => {
        const upgradeError: CRError = {
          errorCode: ChargeRepayErrorCodes.UPGRADE_SUBSCRIPTION_ERROR,
          severity: 'error',
        };
        return throwError(() => upgradeError);
      }),
    );
  }

  public assignSeat(driverId: string) {
    const subscriptionId = this.subscription()?.id;
    if (!subscriptionId) {
      return throwError(
        () =>
          ({
            errorCode: ChargeRepayErrorCodes.ASSIGN_SEAT_ERROR,
            severity: 'error',
          }) as CRError,
      );
    }
    const request: AssignSeatRequest = { seat: driverId };
    return this.api.assignSeat(subscriptionId, request).pipe(
      tap(() => {
        const current = this.subscription();
        if (current) {
          this.subscription.set({
            ...current,
            seatsUsed: [...current.seatsUsed, driverId],
            seatsFreeCount: current.seatsFreeCount - 1,
          });
        }
      }),
      catchError((_error: CRAPIError) => {
        const assignError: CRError = {
          errorCode: ChargeRepayErrorCodes.ASSIGN_SEAT_ERROR,
          severity: 'error',
        };
        return throwError(() => assignError);
      }),
    );
  }

  public unassignSeat(driverId: string) {
    const subscriptionId = this.subscription()?.id;
    if (!subscriptionId) {
      return throwError(
        () =>
          ({
            errorCode: ChargeRepayErrorCodes.UNASSIGN_SEAT_ERROR,
            severity: 'error',
          }) as CRError,
      );
    }
    return this.api.unassignSeat(subscriptionId, driverId).pipe(
      tap(() => {
        const current = this.subscription();
        if (current) {
          this.subscription.set({
            ...current,
            seatsUsed: current.seatsUsed.filter((id) => id !== driverId),
            seatsFreeCount: current.seatsFreeCount + 1,
          });
        }
      }),
      catchError((_error: CRAPIError) => {
        const unassignError: CRError = {
          errorCode: ChargeRepayErrorCodes.UNASSIGN_SEAT_ERROR,
          severity: 'error',
        };
        return throwError(() => unassignError);
      }),
    );
  }
}
